import { Component } from "../lib/component.ts";

interface VideoControlsRefs {
  playPause?: HTMLButtonElement;

  playIcon?: HTMLElement;

  pauseIcon?: HTMLElement;

  mute?: HTMLButtonElement;

  muteIcon?: HTMLElement;

  unmuteIcon?: HTMLElement;
}

interface UnmutedEventDetail {
  source: VideoControls;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function videoHasAudio(video: HTMLVideoElement): boolean {
  const audioTracks: unknown = Reflect.get(video, "audioTracks");
  if (
    audioTracks !== null &&
    typeof audioTracks === "object" &&
    "length" in audioTracks &&
    typeof audioTracks.length === "number" &&
    audioTracks.length > 0
  ) {
    return true;
  }

  if (Reflect.get(video, "mozHasAudio") === true) return true;

  const decoded: unknown = Reflect.get(video, "webkitAudioDecodedByteCount");
  if (typeof decoded === "number" && decoded > 0) return true;

  return false;
}

class VideoControls extends Component<VideoControlsRefs> {
  #video: HTMLVideoElement | null = null;

  #reducedMotion: MediaQueryList = matchMedia("(prefers-reduced-motion: reduce)");

  connectedCallback(): void {
    super.connectedCallback();

    this.#video = this.parentElement?.querySelector("video") ?? null;
    if (!this.#video) return;

    const video = this.#video;
    this.#ensureAriaControls(video);

    this.refs.playPause?.addEventListener("click", this.#togglePlay, {
      signal: this.signal,
    });
    this.refs.mute?.addEventListener("click", this.#toggleMute, {
      signal: this.signal,
    });

    video.addEventListener("play", this.#syncPlayState, { signal: this.signal });
    video.addEventListener("pause", this.#syncPlayState, { signal: this.signal });
    video.addEventListener("volumechange", this.#syncMuteState, {
      signal: this.signal,
    });

    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      this.#detectAudio();
    } else {
      video.addEventListener("loadeddata", () => this.#detectAudio(), {
        once: true,
        signal: this.signal,
      });
    }

    this.#syncPlayState();
    this.#syncMuteState();
    this.#applyReducedMotion();

    this.#reducedMotion.addEventListener("change", this.#applyReducedMotion, {
      signal: this.signal,
    });

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting && this.#video && !this.#video.muted) {
            this.#video.muted = true;
            this.#syncMuteState();
          }
        }
      },
      { threshold: 0 },
    );
    observer.observe(video);
    this.signal.addEventListener("abort", () => observer.disconnect());

    document.addEventListener(
      "video-controls:unmuted",
      (event) => {
        if (!(event instanceof CustomEvent)) return;
        const detail: unknown = event.detail;
        if (isRecord(detail) && detail.source === this) return;
        if (this.#video && !this.#video.muted) {
          this.#video.muted = true;
          this.#syncMuteState();
        }
      },
      { signal: this.signal },
    );
  }

  #togglePlay = (): void => {
    if (!this.#video) return;
    if (this.#video.paused) {
      this.#video.play();
    } else {
      this.#video.pause();
    }
  };

  #toggleMute = (): void => {
    if (!this.#video) return;
    this.#video.muted = !this.#video.muted;
    if (!this.#video.muted) {
      document.dispatchEvent(
        new CustomEvent<UnmutedEventDetail>("video-controls:unmuted", {
          detail: { source: this },
        }),
      );
    }
  };

  #syncPlayState = (): void => {
    if (!this.#video) return;
    const paused = this.#video.paused;
    this.refs.playIcon?.toggleAttribute("hidden", !paused);
    this.refs.pauseIcon?.toggleAttribute("hidden", paused);
    const label = paused
      ? this.refs.playPause?.dataset.labelPlay
      : this.refs.playPause?.dataset.labelPause;
    if (label) {
      this.refs.playPause?.setAttribute("aria-label", label);
    }
  };

  #syncMuteState = (): void => {
    if (!this.#video) return;
    const muted = this.#video.muted;
    this.refs.muteIcon?.toggleAttribute("hidden", muted);
    this.refs.unmuteIcon?.toggleAttribute("hidden", !muted);
    const label = muted ? this.refs.mute?.dataset.labelUnmute : this.refs.mute?.dataset.labelMute;
    if (label) {
      this.refs.mute?.setAttribute("aria-label", label);
    }
  };

  #detectAudio(): void {
    if (!this.#video) return;
    if (videoHasAudio(this.#video)) {
      this.refs.mute?.removeAttribute("hidden");
    }
  }

  #applyReducedMotion = (): void => {
    if (!this.#video) return;
    if (this.#reducedMotion.matches) {
      this.#video.pause();
    }
  };

  #ensureAriaControls(video: HTMLVideoElement): void {
    if (!video.id) {
      video.id = `video-${crypto.randomUUID()}`;
    }
    this.refs.playPause?.setAttribute("aria-controls", video.id);
    this.refs.mute?.setAttribute("aria-controls", video.id);
  }
}

if (!customElements.get("video-controls")) {
  customElements.define("video-controls", VideoControls);
}
